import { useSelector } from 'react-redux';
import { getAllWorkspaces } from '@app/reducers/entities/workspaces';
import { AuditLogTypes } from '@flow/libs/audit-log/constants';

import { T } from '@app/components/translation';

import style from './audit-log-event.module.scss';

type Props = {
  auditLogEvent: Oneflow.AuditLogEvent<typeof AuditLogTypes.TEMPLATE_MOVE>;
  folderNames: Map<number, string>;
  type: string;
};

const getFolderName = (names: Map<number, string>, id?: number | null): string | undefined => {
  if (!id) return;

  // if folder name is not known, show ID
  // this is possible if the folder was deleted
  return names?.get(id) ?? `${id}`;
};

const AgreementTemplateMoveEvent = ({ auditLogEvent, folderNames, type }: Props) => {
  const data = auditLogEvent.information;

  const allWorkspaces = useSelector((state) => getAllWorkspaces(state));
  const fromWorkspace = allWorkspaces.find(
    (workspace) => workspace.id === data.workspace.from,
  )?.name;
  const toWorkspace = allWorkspaces.find((workspace) => workspace.id === data.workspace.to)?.name;
  const fromFolder = getFolderName(folderNames, data.folder?.from);
  const toFolder = getFolderName(folderNames, data.folder?.to);

  const renderAccentText = (text: number | string | null | undefined) =>
    text && <span className={style.Italic}>{text}</span>;

  const StyledId = renderAccentText(auditLogEvent.target?.id);
  const StyledFromWorkspace = renderAccentText(fromWorkspace);
  const StyledToWorkspace = renderAccentText(toWorkspace);
  const StyledFromFolder = renderAccentText(fromFolder);
  const StyledToFolder = renderAccentText(toFolder);

  const isNoFoldersMessage = !fromFolder && !toFolder;
  const isNoStartingFolderMessage = !fromFolder && toFolder;
  const isNoDestinationMessage = fromFolder && !toFolder;

  const renderNoFoldersMessage = () => {
    if (type === 'agreement') {
      return (
        <T
          _str="Moved the document with the ID {id} from the {fromWorkspace} workspace to the {toWorkspace} workspace."
          _comment="Audit log event message for moved group"
          id={StyledId}
          fromWorkspace={StyledFromWorkspace}
          toWorkspace={StyledToWorkspace}
        />
      );
    }
    return (
      <T
        _str="Moved the template with the ID {id} from the {fromWorkspace} workspace to the {toWorkspace} workspace."
        _comment="Audit log event message for moved group"
        id={StyledId}
        fromWorkspace={StyledFromWorkspace}
        toWorkspace={StyledToWorkspace}
      />
    );
  };

  const renderNoStartingFolderMessage = () => {
    if (type === 'agreement') {
      return (
        <T
          _str="Moved the document with the ID {id} from the workspace {fromWorkspace} to the folder {toFolder} in the workspace {toWorkspace}."
          _comment="Audit log event message for moved group"
          id={StyledId}
          fromWorkspace={StyledFromWorkspace}
          toWorkspace={StyledToWorkspace}
          toFolder={StyledToFolder}
        />
      );
    }
    return (
      <T
        _str="Moved the template with the ID {id} from the workspace {fromWorkspace} to the folder {toFolder} in the workspace {toWorkspace}."
        _comment="Audit log event message for moved group"
        id={StyledId}
        fromWorkspace={StyledFromWorkspace}
        toWorkspace={StyledToWorkspace}
        toFolder={StyledToFolder}
      />
    );
  };

  const renderNoDestinationMessage = () => {
    if (type === 'agreement') {
      return (
        <T
          _str="Moved the document with the ID {id} from the folder {fromFolder} in the workspace {fromWorkspace} to the workspace {toWorkspace}."
          _comment="Audit log event message for moved agreement"
          id={StyledId}
          fromWorkspace={StyledFromWorkspace}
          toWorkspace={StyledToWorkspace}
          fromFolder={StyledFromFolder}
        />
      );
    }
    return (
      <T
        _str="Moved the template with the ID {id} from the folder {fromFolder} in the workspace {fromWorkspace} to the workspace {toWorkspace}."
        _comment="Audit log event message for moved template"
        id={StyledId}
        fromWorkspace={StyledFromWorkspace}
        toWorkspace={StyledToWorkspace}
        fromFolder={StyledFromFolder}
      />
    );
  };

  const renderMessage = () => {
    if (type === 'agreement') {
      return (
        <T
          _str="Moved the document with the ID {id} from the folder {fromFolder} in the workspace {fromWorkspace} to the folder {toFolder} in the workspace {toWorkspace}."
          _comment="Audit log event message for moved agreement"
          id={StyledId}
          fromWorkspace={StyledFromWorkspace}
          toWorkspace={StyledToWorkspace}
          fromFolder={StyledFromFolder}
          toFolder={StyledToFolder}
        />
      );
    }
    return (
      <T
        _str="Moved the template with the ID {id} from the folder {fromFolder} in the workspace {fromWorkspace} to the folder {toFolder} in the workspace {toWorkspace}."
        _comment="Audit log event message for moved template"
        id={StyledId}
        fromWorkspace={StyledFromWorkspace}
        toWorkspace={StyledToWorkspace}
        fromFolder={StyledFromFolder}
        toFolder={StyledToFolder}
      />
    );
  };

  return (
    <p data-testid="move-event-text">
      {isNoFoldersMessage
        ? renderNoFoldersMessage()
        : isNoStartingFolderMessage
          ? renderNoStartingFolderMessage()
          : isNoDestinationMessage
            ? renderNoDestinationMessage()
            : renderMessage()}
    </p>
  );
};

export default AgreementTemplateMoveEvent;
